import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import ReferralContext from './ReferralContext';
import useUser from '../UserContext/useUser';
import { fetchState } from '../UserContext';
import {
  buildReferralInfo,
  getAffiliateInfo,
  isSameBrowserOwnReferral,
  parseReferralCookie,
  setAffiliateCookie,
  setReferralCookie,
} from './referralUtils';
import { fetchMyReferrerProfile, fetchPublicReferralByCode } from '../../api/referrals';
import { getCookie, removeCookie } from '../../util/cookies';

const isLoggedInUser = (userFetchState, user) =>
  userFetchState === fetchState.COMPLETE && Object.keys(user).length > 0;

const getIncomingReferralCode = () => {
  if (typeof window === 'undefined') return null;

  return new URLSearchParams(window.location.search).get('referralCode')?.trim() || null;
};

const isInvalidReferralPath = (pathOrSearch = '') => {
  const search = pathOrSearch.includes('?')
    ? pathOrSearch.slice(pathOrSearch.indexOf('?'))
    : pathOrSearch;

  return new URLSearchParams(search).get('utm') === 'invalidreferral';
};

export default function ReferralProvider({ children }) {
  const router = useRouter();
  const { user, userFetchState } = useUser();
  const [referralInfo, setReferralInfo] = useState({});
  const [affiliateInfo, setAffiliateInfo] = useState();
  const [invalidReferral, setInvalidReferral] = useState(false);
  const processedIncomingCodeRef = useRef(null);
  const isInvalidReferralRoute = isInvalidReferralPath(router.asPath);
  const shouldSuppressAttribution = invalidReferral || isInvalidReferralRoute;

  useEffect(() => {
    if (!isInvalidReferralRoute) return;

    processedIncomingCodeRef.current = null;
    removeCookie('referral');
    setInvalidReferral(true);
    setReferralInfo({});
    setAffiliateInfo({});
  }, [isInvalidReferralRoute]);

  useEffect(() => {
    if (shouldSuppressAttribution) {
      setAffiliateInfo({});
      return;
    }

    const nextAffiliateInfo = getAffiliateInfo();
    if (nextAffiliateInfo?.code && !nextAffiliateInfo?.hasCookie) {
      setAffiliateCookie(nextAffiliateInfo);
    }

    setAffiliateInfo(nextAffiliateInfo);
  }, [router.asPath, shouldSuppressAttribution]);

  useEffect(() => {
    if (shouldSuppressAttribution) {
      removeCookie('referral');
      setReferralInfo({});
      return;
    }

    if (getIncomingReferralCode()) return;

    processedIncomingCodeRef.current = null;
    setInvalidReferral(false);

    const existingReferral = getCookie('referral');
    if (existingReferral?.includes('code|')) {
      setReferralInfo(parseReferralCookie(existingReferral));
      return;
    }

    setReferralInfo({});
  }, [router.asPath, shouldSuppressAttribution]);

  useEffect(() => {
    if (userFetchState === fetchState.PENDING) return undefined;

    const incomingCode = getIncomingReferralCode();
    if (!incomingCode) return undefined;
    if (processedIncomingCodeRef.current === incomingCode) return undefined;

    processedIncomingCodeRef.current = incomingCode;
    setInvalidReferral(false);

    let cancelled = false;

    const resolveIncomingReferral = async () => {
      setReferralInfo({});

      const publicReferrer = await fetchPublicReferralByCode(incomingCode);
      if (cancelled) return;

      if (publicReferrer.status !== 200 || !publicReferrer.data?.referralCode) {
        return;
      }

      if (isLoggedInUser(userFetchState, user)) {
        const currentReferrer = await fetchMyReferrerProfile();
        if (cancelled) return;

        if (
          currentReferrer.status === 200 &&
          currentReferrer.data?.referralCode === publicReferrer.data.referralCode
        ) {
          removeCookie('referral');
          setInvalidReferral(true);
          setReferralInfo({});
          router.replace('/?utm=invalidreferral');
          return;
        }
      } else if (isSameBrowserOwnReferral(publicReferrer.data.referralCode)) {
        removeCookie('referral');
        setInvalidReferral(true);
        setReferralInfo({});
        router.replace('/?utm=invalidreferral');
        return;
      }

      setReferralCookie(publicReferrer.data.name, publicReferrer.data.referralCode);
      setReferralInfo(buildReferralInfo(publicReferrer.data));
    };

    resolveIncomingReferral().catch((error) => {
      console.error('Failed to resolve referral code', error);
      setReferralInfo({});
    });

    return () => {
      cancelled = true;
    };
  }, [router, router.asPath, user, userFetchState]);

  return (
    <ReferralContext.Provider
      value={useMemo(() => ({
        referralInfo: shouldSuppressAttribution ? {} : referralInfo,
        affiliateInfo: shouldSuppressAttribution ? {} : affiliateInfo,
        invalidReferral: shouldSuppressAttribution,
      }), [affiliateInfo, referralInfo, shouldSuppressAttribution])}>
      {children}
    </ReferralContext.Provider>
  );
}

ReferralProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
